import { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { ChevronDown, Newspaper } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';

interface NewsItem {
  systemId: string;
  entryNo: number;
  date: string;
  header: string;
  body: string;
}

const longDate = (date: string) =>
  new Date(date).toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });

export default function News() {
  const [openId, setOpenId] = useState<string | null>(null);

  const { data: vendorData } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  const { data: newsData, isLoading, isError, error: newsError } = useQuery({
    queryKey: ['vendor', 'news'],
    staleTime: 60 * 1000,
    queryFn: async () => (await vendorApi.getNews()).data,
  });

  const news: NewsItem[] = newsData?.news || [];
  const [latest, ...earlier] = news;

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Nyheter</h1>
          <p className="mt-2 text-gray-600">Information från din åkericentral</p>

          {isLoading ? (
            <div className="card mt-8 p-10 text-center text-gray-500">Laddar nyheter…</div>
          ) : isError ? (
            <p role="alert" className="card mt-8 p-6 text-sm text-red-800">
              {(newsError as any)?.response?.data?.code === 'BC_SERVICE_MISSING'
                ? 'Nyheterna är inte tillgängliga än – Business Central behöver uppdateras.'
                : 'Nyheterna kunde inte hämtas. Försök igen om en stund.'}
            </p>
          ) : !latest ? (
            <div className="card mt-8 flex flex-col items-center gap-3 p-12 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full bg-gray-50 ring-1 ring-inset ring-gray-100">
                <Newspaper className="h-6 w-6 text-gray-400" aria-hidden />
              </span>
              <h2 className="text-base font-semibold text-ink">Inga nyheter just nu</h2>
              <p className="max-w-[46ch] text-sm text-gray-500">
                När din åkericentral publicerar information visas den här.
              </p>
            </div>
          ) : (
            <div className="mt-8 grid gap-8 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
              {/* Latest, in full */}
              <article className="card card-accent p-7 sm:p-9">
                <p className="flex items-center gap-3 text-xs">
                  <span className="rounded-full px-2.5 py-0.5 font-medium uppercase tracking-[0.14em] text-brass ring-1 ring-inset ring-[#ecd9a4]">
                    Senaste
                  </span>
                  <time dateTime={latest.date} className="text-gray-500">
                    {longDate(latest.date)}
                  </time>
                </p>
                <h2 className="mt-4 text-balance text-2xl font-semibold tracking-tight text-ink">{latest.header}</h2>
                <p className="mt-4 max-w-[65ch] whitespace-pre-line text-[15px] leading-relaxed text-gray-700">
                  {latest.body}
                </p>
              </article>

              {/* Earlier, expandable */}
              <section aria-labelledby="earlier-news" className="card p-6">
                <h2 id="earlier-news" className="text-[15px] font-semibold text-ink">
                  Tidigare nyheter
                </h2>
                {earlier.length === 0 ? (
                  <p className="mt-4 text-sm text-gray-500">Inga tidigare nyheter.</p>
                ) : (
                  <ul className="mt-3 divide-y divide-gray-100">
                    {earlier.map((item) => {
                      const open = openId === item.systemId;
                      return (
                        <li key={item.systemId}>
                          <button
                            type="button"
                            aria-expanded={open}
                            onClick={() => setOpenId(open ? null : item.systemId)}
                            className="group flex w-full items-start gap-3 py-3 text-left"
                          >
                            <span className="min-w-0 flex-1">
                              <time dateTime={item.date} className="block text-xs text-gray-500">
                                {longDate(item.date)}
                              </time>
                              <span className="mt-0.5 block text-sm font-medium text-ink group-hover:text-blue-600">
                                {item.header}
                              </span>
                            </span>
                            <ChevronDown
                              className={`mt-3 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-300 ease-fluid ${open ? 'rotate-180' : ''}`}
                              aria-hidden
                            />
                          </button>
                          {open && (
                            <p className="whitespace-pre-line pb-4 text-sm leading-relaxed text-gray-700">{item.body}</p>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
